import { InputStepper, UI, HStack, Stack, VStack } from '@genial/components';
import { BoxBorder, Color, Spacing, Typography } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import { Box, Button, TextArea } from 'native-base';
import React, { useState } from 'react';

import { InputMoney } from '../../../../../../components/InputMoney';
import { DirectTreasuryWithdrawAssetNormalized } from '../../../../../../store/redemptionSolicitation/types';
import { CardTitle } from '../../../../components/CardTitle';
import { InfoItem } from '../../../../components/InfoItem';
import { ProductCard } from '../../../../components/ProductCard';
import { useDirectTreasuryRedemptionController } from '../../../../hooks/useDirectTreasuryRedemptionController';

export interface ConfirmationStepInfos {
  quantityToRedeem: number;
  totalValue: number;
  channel: string;
  observation: string;
}

export interface ValueAndConfirmationStepProps {
  handleCancel: () => void;
  handleConfirm: (infos: ConfirmationStepInfos) => void;
  treasury: DirectTreasuryWithdrawAssetNormalized;
  isExecuting: boolean;
}

export const ValueAndConfirmationStep: React.FC<ValueAndConfirmationStepProps> = ({
  handleCancel,
  handleConfirm,
  treasury,
  isExecuting,
}) => {
  const [selectedOption, setSelectedOption] = useState('phone');
  const [textAreaInput, setTextAreaInput] = useState('');

  const {
    handleInputBlur,
    handleInputClear,
    handleInputFocus,
    handleSubmit,
    control,
    isFocused,
    valueAppliedFormatted,
    amountItem,
    handleChangeAmountItem,
  } = useDirectTreasuryRedemptionController(
    treasury.minValueNormalized,
    treasury.maxValueNormalized,
    treasury.minimumStepQuantity,
    treasury.saleValue
  );

  const handlePressConfirm = () => {
    handleConfirm({
      channel: selectedOption,
      observation: textAreaInput,
      quantityToRedeem: amountItem,
      totalValue: treasury.saleValue * amountItem,
    });
  };

  return (
    <VStack space={Spacing['stack.xxxs']}>
      <ProductCard barColor="product.tesouro">
        <VStack space={Spacing['stack.xxxs']}>
          <CardTitle title={treasury.productNameNormalized} />

          <InfoItem label="Vencimento" value={treasury.maturityDateFormatted} />
          <InfoItem label="Indexador" value={treasury.indexNormalized} />
          <InfoItem label="Quantidade disponível" value={treasury.availableQuantityNormalized} />
          <InfoItem label="Taxa de venda" value={treasury.sellTaxNormalized} />
          <InfoItem label="Preço Unitário" value={treasury.unitPriceNormalized} />

          <VStack space={Spacing['stack.quarck']} w="full">
            <UI.Text color="neutral.black" variant="semibold">
              Quantidade desejada
            </UI.Text>

            <InputStepper
              name="direct-treasury-redemption-input"
              stepperValue={0.01}
              max={treasury.availableQuantityForSale}
              min={treasury.minimumStepQuantity}
              keyboardType="numeric"
              fontSize={Typography['fontsize.md']}
              defaultValue={treasury.minimumStepQuantity}
              onChange={handleChangeAmountItem}
              showErrorMessage={false}
            />
          </VStack>

          <VStack space={Spacing['stack.quarck']} w="full">
            <UI.Text color="neutral.black" variant="semibold">
              Valor estimado
            </UI.Text>

            <InputMoney
              id="direct-treasury-redemption-input"
              name="redemptionValue"
              control={control}
              handleFocus={handleInputFocus}
              handleBlur={handleInputBlur}
              handleSubmit={handleSubmit}
              handleClear={handleInputClear}
              currentValue={valueAppliedFormatted}
              hasFocus={isFocused}
              minValue={0}
              hideEditIconOnDisabled
              isDisabled
            />
          </VStack>

          <UI.Text variant="semibold" color="neutral.black">
            Cliente confirmou o resgate
          </UI.Text>

          <VStack space={Spacing['stack.quarck']} w="full">
            <UI.Text color="neutral.darkest">Selecione o canal</UI.Text>
            <Stack bgColor={Color.background} borderRadius={BoxBorder.thick}>
              <UI.Select
                placeholder="Selecione o canal"
                options={[
                  { label: 'Via Telefone', value: 'phone' },
                  { label: 'Via E-mail', value: 'email' },
                  { label: 'WhatsApp', value: 'whatsApp' },
                ]}
                selectedValue={selectedOption}
                onValueChange={setSelectedOption}
              />
            </Stack>
          </VStack>

          <VStack space={Spacing['stack.quarck']} w="full">
            <UI.Text color="neutral.darkest">Observação</UI.Text>
            <Box height={137}>
              <TextArea
                flex={1}
                borderRadius={Spacing['inline.nano']}
                backgroundColor={Color.background}
                borderColor={'transparent'}
                _focus={{
                  backgroundColor: 'transparent',
                  borderColor: Color['brand.primary.principal'],
                  borderWidth: 1,
                }}
                _hover={{ borderColor: Color['brand.primary.principal'] }}
                value={textAreaInput}
                placeholder="Existe alguma informação sobre essa autorização"
                autoCompleteType={'off'}
                onChangeText={setTextAreaInput}
              />
            </Box>
          </VStack>
        </VStack>
      </ProductCard>
      <HStack
        alignSelf="end"
        space={Spacing['inline.xxxs']}
        w={Utils.Responsive.DesktopPhone({ phone: 'full', desktop: 'auto' })}
        justifyContent={Utils.Responsive.DesktopPhone({
          phone: 'space-between',
          desktop: 'inherit',
        })}>
        <UI.Button
          variant="secondary"
          onPress={handleCancel}
          w={Utils.Responsive.DesktopPhone({ phone: '45%', desktop: 'auto' })}>
          Cancelar
        </UI.Button>
        <Button
          variant="callToAction"
          onPress={handlePressConfirm}
          isDisabled={false}
          isLoadingText="Confirmando..."
          spinnerPlacement="end"
          isLoading={isExecuting}
          w={Utils.Responsive.DesktopPhone({ phone: '45%', desktop: 'auto' })}>
          Confirmar
        </Button>
      </HStack>
    </VStack>
  );
};
